// @flow

import { put, call, all, takeEvery, select } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import config from "@config";
import { sortToString } from "@transformers/sort";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import * as actions from "@modules/service/constants/actions";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import type {
  NewEmployeeSmsRule,
  EmployeeSmsRule,
} from "@modules/service/reducer/employee-sms-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type EmployeeSmsNotificationsListRequest = $Exact<{
  clientId: string,
  sort: string,
  page: number,
}>;

export function* onGetSmsRules({
  payload,
}: Action<EmployeeSmsNotificationsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<EmployeeSmsRule>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const currentPage = yield select(serviceSelectors.getEmployeeSmsRulesPage);
    const page = (payload && payload.page) || currentPage;
    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt`,
      {
        params: {
          page,
          sort: payload.sort ? sortToString(payload.sort) : "",
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetEmployeeSmsRulesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSmsRulesError(e.response));
  }
}

type EmployeeSmsSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetEmployeeSmsSchema({
  payload,
}: Action<EmployeeSmsSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("SmsWellbeingPromptDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetEmployeeSmsSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSmsSchemaError(e.response));
  }
}

type AddEmployeeSmsRuleRequest = $Exact<{
  clientId: string,
  rule: NewEmployeeSmsRule,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onAddSmsRule({
  payload,
}: Action<AddEmployeeSmsRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: newRule } = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt`,
      payload.rule,
    );
    if (!newRule) {
      throw Error("Failed to create new rule");
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${newRule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddEmployeeSmsRuleSuccess()),
      put(actionCreators.GetEmployeeSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowAddEmployeeSmsModal(false)),
      put(sendSuccessMessage("SMS_WELLNESS_PROMPT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmployeeSmsRuleError(e.response));
  }
}

type UpdateEmployeeSmsRuleRequest = $Exact<{
  clientId: string,
  rule: EmployeeSmsRule,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateSmsRule({
  payload,
}: Action<UpdateEmployeeSmsRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${payload.rule.id}`,
      payload.rule,
    );

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${payload.rule.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignment) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${
              payload.clientId
            }/service-config/absence-management/sms-wellbeing-prompt/${
              payload.rule.id
            }/assignment/${a.id || ""}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${payload.rule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.UpdateEmployeeSmsRuleSuccess()),
      put(actionCreators.GetEmployeeSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowEditEmployeeSmsModal(false)),
      put(sendSuccessMessage("SMS_WELLNESS_PROMPT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeSmsRuleError(e.response));
  }
}

type DeleteEmployeeSmsRuleRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onDeleteSmsRule({
  payload,
}: Action<DeleteEmployeeSmsRuleRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${payload.ruleId}`,
    );

    yield all([
      put(actionCreators.DeleteEmployeeSmsRuleSuccess()),
      put(actionCreators.GetEmployeeSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowDeleteEmployeeSmsModal(false)),
      put(sendSuccessMessage("SMS_WELLNESS_PROMPT_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteEmployeeSmsRuleError(e.response));
  }
}

type EmployeeSmsNotificationAssignmentsRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onGetSmsAssignments({
  payload,
}: Action<EmployeeSmsNotificationAssignmentsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<HierarchyAssignment>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-wellbeing-prompt/${payload.ruleId}/assignment`,
    );

    yield put(actionCreators.GetEmployeeSmsAssignmentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSmsAssignmentsError(e.response));
  }
}

// ROOT

export function* EmployeeSmsNotificationsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetEmployeeSmsRules, onGetSmsRules),
    takeEvery(actions.GetEmployeeSmsSchema, onGetEmployeeSmsSchema),
    takeEvery(actions.AddEmployeeSmsRule, onAddSmsRule),
    takeEvery(actions.UpdateEmployeeSmsRule, onUpdateSmsRule),
    takeEvery(actions.DeleteEmployeeSmsRule, onDeleteSmsRule),
    takeEvery(actions.GetEmployeeSmsAssignments, onGetSmsAssignments),
  ]);
}

export default EmployeeSmsNotificationsSagas;
